import Layout from "../components/layout/Layout";
import SEO, { orgSchema, breadcrumbSchema } from "../components/layout/SEO";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import SectionHeading from "../components/home/SectionHeading";
import Results from "../components/home/Results";
import CaseReviewSection from "../components/home/CaseReviewSection";
import { Reveal, StaggerGroup, StaggerItem } from "../components/shared/Reveal";
import { Check } from "lucide-react";
import { SITE } from "../data/site";
import { useI18n } from "../i18n";

const COST_FACTORS = [
  "Procedure complexity", "Surgeon", "Hospital or facility", "Anaesthesia",
  "Implants or medical devices", "Hospital nights", "Tests", "Garments",
  "Medication", "Follow-up care", "Revision requirements",
];

const STAY_FACTORS = [
  "Type of surgery", "Follow-up appointments", "Suture removal",
  "Swelling and mobility", "Risk of complications", "Surgeon's travel-clearance advice",
];

// /plastic-surgery-cost-vietnam
export function CostPage() {
  const { lang } = useI18n();
  return (
    <Layout>
      <SEO title={`What Does Plastic Surgery in Vietnam Cost? | ${SITE.name}`} description="Plastic surgery cost in Vietnam depends on many factors. Understand what an indicative quotation may include before you travel." path="/plastic-surgery-cost-vietnam" noindex={lang !== "en"} schema={[orgSchema(), breadcrumbSchema([{ name: "Home", to: "/" }, { name: "Costs", to: "/plastic-surgery-cost-vietnam" }])]} />
      <Breadcrumbs items={[{ name: "Costs" }]} />
      <section className="container-psv py-12 md:py-20">
        <SectionHeading eyebrow="Costs" heading="What does plastic surgery in Vietnam cost?" intro="We do not publish fixed prices. Cost depends on your individual case, and an indicative quotation follows an assessment. Any quotation may change if clinical findings differ." />
        <StaggerGroup className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 max-w-4xl">
          {COST_FACTORS.map((f) => (
            <StaggerItem key={f} className="flex items-center gap-3 py-3 border-b border-border">
              <Check className="w-5 h-5 text-accent shrink-0" strokeWidth={1.5} /><span className="text-[15px] text-secondary-foreground">{f}</span>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>
      <CaseReviewSection />
    </Layout>
  );
}

// /plastic-surgery-safety-vietnam
export function SafetyPage() {
  const { lang } = useI18n();
  return (
    <Layout>
      <SEO title={`Is Plastic Surgery in Vietnam Safe? | ${SITE.name}`} description="Safety depends on the surgeon, facility, anaesthesia, your health, the procedure and aftercare — not simply the country." path="/plastic-surgery-safety-vietnam" noindex={lang !== "en"} schema={[orgSchema(), breadcrumbSchema([{ name: "Home", to: "/" }, { name: "Safety", to: "/plastic-surgery-safety-vietnam" }])]} />
      <Breadcrumbs items={[{ name: "Safety" }]} />
      <section className="container-psv py-12 md:py-20 max-w-3xl">
        <SectionHeading eyebrow="Safety" heading="Is plastic surgery in Vietnam safe?" intro="Safety is not decided by a country. It depends on the individual surgeon, the facility, anaesthesia, your own health, the specific procedure and the quality of your aftercare." />
        <Reveal className="mt-10 space-y-6 text-[16px] text-secondary-foreground leading-relaxed">
          <p>Ask where surgery will take place and whether the facility is appropriately licensed. Ask about the surgeon’s credentials and how to verify them. Ask how complications would be handled and what follow-up is available if you travel home.</p>
          <p>No responsible professional guarantees results. A good consultation discusses realistic outcomes, limitations and the possibility that a revision may sometimes be needed.</p>
          <h2 className="font-serif text-3xl text-primary pt-4">How long should I stay in Vietnam?</h2>
          <p>Your required stay depends on several factors:</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {STAY_FACTORS.map((s) => (
              <li key={s} className="flex items-center gap-2.5"><Check className="w-5 h-5 text-accent shrink-0" strokeWidth={1.5} />{s}</li>
            ))}
          </ul>
        </Reveal>
      </section>
      <CaseReviewSection />
    </Layout>
  );
}

// /results
export function ResultsPage() {
  const { lang } = useI18n();
  return (
    <Layout>
      <SEO title={`Results & Patient Stories | ${SITE.name}`} description="Real experiences, realistic expectations. We use only consented content and never digitally alter clinical results." path="/results" noindex={lang !== "en"} schema={[orgSchema()]} />
      <Breadcrumbs items={[{ name: "Results" }]} />
      <div className="pt-4"><Results /></div>
      <CaseReviewSection />
    </Layout>
  );
}
